import React, {
  useRef,
  useCallback,
  useEffect,
  // useMemo,
  Dispatch,
  SetStateAction,
  useState,
} from 'react';
import * as Yup from 'yup';
import { useSnackbar } from 'notistack';
import { preventLeadingWhitespace } from '@/utils/helperFunctions';
import clsx from 'clsx';
import { textFieldValidation } from '@/utils/validations';
import { formatDisplayDate } from '@/utils/Date';
import { Modal } from '@/components';
import { ModalProps } from '@mui/base/Modal';
import { IBoatData } from '@/types/boat-management.types';
import { useGetUsersQuery } from '@/services/UserManagementService';
import { UserRoles } from '@/constant';
import { UserResponse } from '@/types/user-management.types';
import { PaginatedApiResponse } from '@/types/api.types';
import NumberInput from '@/components/inputControls/NumberInput';
// import { useGetBoatModelsQuery } from "@/services/BoatManagementService";

// Validation schema for create/edit boat
const boatValidationSchema = Yup.object().shape({
  name: textFieldValidation('Boat Name', 2, 50, true),
  // model: textFieldValidation("Boat Model", 2, 50, true),
  type: Yup.string().required('Boat type is required'),
  length: Yup.string()
    .required('Length is required')
    .matches(
      /^\d+(?:[.,]\d{1,2})?$/,
      "Length must be a number with up to 2 decimals, and only one of ',' or '.' allowed"
    ),
  width: Yup.string()
    .required('Width is required')
    .matches(
      /^\d+(?:[.,]\d{1,2})?$/,
      "Width must be a number with up to 2 decimals, and only one of ',' or '.' allowed"
    ),
  draft: Yup.string()
    .matches(/^[0-9]+([,.][0-9]+)?$/, 'Draft must be a valid number')
    .nullable(),
  height: Yup.string()
    .matches(/^[0-9]+([,.][0-9]+)?$/, 'Height must be a valid number')
    .nullable(),
  weight: Yup.string()
    .matches(/^[0-9]+([,.][0-9]+)?$/, 'Weight must be a valid number')
    .nullable(),
  registrationNumber: textFieldValidation('Registration Number', 3, 20, true),
  boaterId: Yup.string().required('Boater is required'),
  unit: Yup.string().oneOf(['m', 'ft']).required('Unit is required'),
});

interface BoaterOption {
  id: string;
  name: string;
  email?: string;
}

interface BoatModalProps extends Omit<ModalProps, 'children' | 'onSubmit'> {
  mode: 'view' | 'edit' | 'create';
  formData: Partial<IBoatData> & { unit?: 'm' | 'ft' };
  setFormData: Dispatch<
    SetStateAction<Partial<IBoatData> & { unit?: 'm' | 'ft' }>
  >;
  errors: { [key: string]: string };
  setErrors: Dispatch<SetStateAction<{ [key: string]: string }>>;
  onSubmit: (isCustomModel: boolean, boatModelId?: string) => Promise<void>;
  isSubmitting?: boolean;
}

type BoatDimensionField = 'length' | 'width' | 'draft' | 'height' | 'weight';

const BoatModal = ({
  mode,
  open,
  onClose,
  formData,
  setFormData,
  errors,
  setErrors,
  onSubmit,
  isSubmitting,
  className,
}: BoatModalProps) => {
  const { enqueueSnackbar } = useSnackbar();
  // const modelInputRef = useRef<HTMLInputElement>(null);
  const modelDropdownRef = useRef<HTMLDivElement>(null);
  const boaterDropdownRef = useRef<HTMLDivElement>(null);
  const nameInputRef = useRef<HTMLInputElement>(null);
  const [boaterOptions, setBoaterOptions] = useState<BoaterOption[]>([]);
  const [isBoaterDropdownOpen, setIsBoaterDropdownOpen] = useState(false);
  const [, setModelSearch] = useState('');
  const [, setIsModelDropdownOpen] = useState(false);
  const [isCustomModel] = useState<boolean>(false);
  const [selectedBoatModelId] = useState<string | undefined>(undefined);
  const validateField = useCallback(
    async (
      name: keyof IBoatData | 'boaterId' | 'unit',
      value: IBoatData[typeof name] | 'm' | 'ft'
    ) => {
      try {
        await boatValidationSchema.validateAt(name, { [name]: value });
        setErrors((prev) => ({ ...prev, [name]: '' }));
      } catch (err) {
        if (err instanceof Yup.ValidationError) {
          setErrors((prev) => ({ ...prev, [name]: err.message }));
        }
      }
    },
    [setErrors]
  );

  // Fetch boater list when modal opens
  const {
    data: usersData,
    isLoading: isUsersLoading,
    error: usersError,
  } = useGetUsersQuery(
    {
      // limit: 10,
      roles: [UserRoles.BOATER],
    },
    { skip: !open }
  ) as {
    data: PaginatedApiResponse<UserResponse[]> | undefined;
    isLoading: boolean;
    error: boolean;
  };

  // Set boater options from API response
  useEffect(() => {
    if (usersData?.data) {
      const options = usersData.data.map((user: UserResponse) => ({
        id: user.id,
        name: user.name,
        email: user.email,
      }));
      setBoaterOptions(options);

      // When in edit mode with a boater ID, ensure we have the name
      if ((mode === 'edit' || mode === 'view') && formData.boater?.id && open) {
        const selectedBoater = usersData.data.find(
          (user: UserResponse) => user.id === formData.boater?.id
        );

        if (selectedBoater) {
          // Only update if name is missing or different
          if (
            !formData.boater.name ||
            formData.boater.name !== selectedBoater.name
          ) {
            setFormData((prev) => ({
              ...prev,
              boater: {
                id: selectedBoater.id,
                name: selectedBoater.name,
                email: selectedBoater.email,
              },
            }));
            validateField('boaterId', selectedBoater.id);
          }
        }
      }
    }
  }, [
    usersData,
    mode,
    formData.boater?.id,
    formData.boater?.name,
    open,
    setFormData,
    validateField,
  ]);

  // Handle API errors
  useEffect(() => {
    if (usersError) {
      console.error('Failed to load boaters:', usersError);
      enqueueSnackbar('Failed to load boaters', { variant: 'error' });
    }
  }, [usersError, enqueueSnackbar]);

  // Effect to set model search and ensure boat data is properly initialized when the modal opens
  useEffect(() => {
    if (open) {
      // Always set model search when opening modal
      if (formData.model) {
        setModelSearch(formData.model);
      }
      // Set default unit to 'ft' when modal opens in create mode
      if (mode === 'create' && !formData.unit) {
        setFormData((prev) => ({ ...prev, unit: 'ft' }));
        validateField('unit', 'ft');
      }
    }
  }, [
    open,
    formData.model,
    formData.boater,
    formData.isCustomBoatModel,
    formData.unit,
    mode,
    setFormData,
    validateField,
  ]);

  // Track when the boaterOptions are loaded for the first time in edit mode
  useEffect(() => {
    if (
      open &&
      mode === 'edit' &&
      formData.boater?.id &&
      boaterOptions.length > 0
    ) {
      // Find the selected boater in the options
      const selectedBoater = boaterOptions.find(
        (boater) => boater.id === formData.boater?.id
      );

      if (selectedBoater) {
        // Update the form data with the complete boater information
        setFormData((prev) => ({
          ...prev,
          boater: {
            id: selectedBoater.id,
            name: selectedBoater.name,
            email: selectedBoater.email,
          },
        }));
        validateField('boaterId', selectedBoater.id);
      }
    }
  }, [
    open,
    mode,
    formData.boater?.id,
    boaterOptions,
    setFormData,
    validateField,
  ]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        modelDropdownRef.current &&
        !modelDropdownRef.current.contains(event.target as Node)
      ) {
        setIsModelDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close boater dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        boaterDropdownRef.current &&
        !boaterDropdownRef.current.contains(event.target as Node)
      ) {
        setIsBoaterDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Autofocus the Name field when the modal opens (except in view mode)
  useEffect(() => {
    if (open && mode !== 'view') {
      const t = setTimeout(() => nameInputRef.current?.focus(), 100);
      return () => clearTimeout(t);
    }
  }, [open, mode]);

  // Prevent mouse wheel from changing number inputs
  useEffect(() => {
    const preventWheel = (e: WheelEvent) => {
      const target = e.target as HTMLElement;
      if (
        target.tagName === 'INPUT' &&
        (target as HTMLInputElement).type === 'number'
      ) {
        e.preventDefault();
      }
    };
    document.addEventListener('wheel', preventWheel, { passive: false });
    return () => document.removeEventListener('wheel', preventWheel);
  }, []);

  const handleBoaterDropdownToggle = () => {
    // Don't open dropdown in view mode or while loading users
    if (mode === 'view' || isUsersLoading) {
      return;
    }
    setIsBoaterDropdownOpen(!isBoaterDropdownOpen);
  };

  const handleBoaterSelect = (boater: BoaterOption) => {
    setFormData((prev) => ({
      ...prev,
      boater: {
        id: boater.id,
        name: boater.name || boater.email || '',
      },
    }));
    validateField('boaterId', boater.id);
    setIsBoaterDropdownOpen(false);
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    if (name === 'boaterId') {
      const selectedBoater = boaterOptions.find(
        (boater) => boater.id === value
      );
      setFormData((prev) => ({
        ...prev,
        boater: {
          id: value,
          name: selectedBoater?.name || selectedBoater?.email || '',
        },
      }));
      validateField('boaterId', value);
    } else if (name === 'model') {
      // Always sync the model value with modelSearch state
      setFormData((prev) => ({
        ...prev,
        model: value,
        // Only set as custom model if we're already in custom mode
        isCustomBoatModel: isCustomModel,
      }));
      setModelSearch(value);
      validateField('model', value);
    } else if (
      name === 'length' ||
      name === 'width' ||
      name === 'draft' ||
      name === 'height' ||
      name === 'weight'
    ) {
      // Store as string, allow only digits, comma, and dot
      const sanitized = value.replace(/[^\d.,]/g, '');
      setFormData((prev) => ({
        ...prev,
        [name]: sanitized,
      }));
      validateField(name as keyof IBoatData, sanitized);
    } else if (name === 'unit') {
      setFormData((prev) => ({
        ...prev,
        unit: value as 'm' | 'ft',
      }));
      validateField('unit', value);
    } else if (name === 'type') {
      setFormData((prev) => ({
        ...prev,
        type: value,
      }));
      validateField('type', value);
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
      validateField(name as keyof IBoatData, value);
    }
  };

  const handleDimensionChange = (name: BoatDimensionField) => {
    return (value: number | string | undefined) => {
      const nextValue =
        value === undefined ? '' : String(value).replace('.', ',');
      setFormData((prev) => ({
        ...prev,
        [name]: nextValue,
      }));
      validateField(name, nextValue);
    };
  };

  const handleCancelClick = () => {
    onClose?.({}, 'backdropClick');
  };

  const handleSubmit = async () => {
    await onSubmit(isCustomModel, selectedBoatModelId);
  };

  // Determine if fields should be disabled based on mode and custom model status
  const isFieldDisabled = () => {
    if (isSubmitting || mode === 'view') return true;
    return false;
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      className={clsx('!flex items-center justify-center', className)}
      aria-labelledby="boat-modal-title"
      aria-describedby="boat-modal-description"
      role="dialog"
      aria-modal="true"
    >
      <div
        className="bg-gray-100 p-6 w-full max-w-md mx-4 rounded-lg shadow-lg flex flex-col max-h-[97dvh] overflow-y-auto"
        role="document"
      >
        <h1
          id="boat-modal-title"
          className="text-lg font-medium text-gray-900 mb-4"
        >
          {mode === 'create'
            ? 'Create Boat'
            : mode === 'edit'
              ? 'Edit Boat'
              : 'View Boat'}
        </h1>
        <div id="boat-modal-description" className="sr-only">
          {mode === 'create'
            ? 'Form to create a new boat'
            : mode === 'edit'
              ? 'Form to edit boat details'
              : 'View boat details'}
        </div>
        <div
          className="flex-1 overflow-y-auto max-h-[70vh] custom-scrollbar"
          role="form"
          aria-label={`${mode === 'create' ? 'Create' : mode === 'edit' ? 'Edit' : 'View'} boat form`}
        >
          <div className="flex flex-col gap-4 pr-3">
            {/* Boat Name Field */}
            <div>
              <label
                htmlFor="name-input"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Boat Name <span className="text-red-500">*</span>
              </label>
              <input
                id="name-input"
                type="text"
                name="name"
                value={formData.name || ''}
                ref={nameInputRef}
                onChange={handleInputChange}
                onKeyDown={preventLeadingWhitespace}
                disabled={mode === 'view'}
                required={mode !== 'view'}
                className={clsx(
                  'input input-bordered w-full h-10',
                  errors.name && 'border-red-500 focus:ring-red-500'
                )}
                autoComplete="name"
                aria-invalid={!!errors.name}
                aria-describedby={errors.name ? 'name-error' : undefined}
                aria-required="true"
                placeholder="Enter boat name"
              />
              {errors.name && (
                <p
                  id="name-error"
                  className="text-red-500 text-sm mt-1"
                  role="alert"
                >
                  {errors.name}
                </p>
              )}
            </div>

            {/* Boat Type Field */}
            <div>
              <label
                htmlFor="type-input"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Boat Type <span className="text-red-500">*</span>
                {isCustomModel && (
                  <span className="text-blue-600 text-xs ml-1">(Editable)</span>
                )}
              </label>
              <select
                id="type-input"
                name="type"
                value={formData.type || ''}
                onChange={handleInputChange}
                disabled={isFieldDisabled()}
                required={mode !== 'view'}
                className={clsx(
                  'input input-bordered w-full h-10',
                  errors.type && 'border-red-500 focus:ring-red-500'
                )}
                autoComplete="off"
                aria-invalid={!!errors.type}
                aria-describedby={errors.type ? 'type-error' : undefined}
                aria-required="true"
              >
                <option value="Sailboat">Sailboat</option>
                <option value="Motorboat">Motorboat</option>
                <option value="Yacht">Yacht</option>
                <option value="Catamaran">Catamaran</option>
                <option value="Fishing Boat">Fishing Boat</option>
                <option value="Speedboat">Speedboat</option>
              </select>
              {errors.type && (
                <p
                  id="type-error"
                  className="text-red-500 text-sm mt-1"
                  role="alert"
                >
                  {errors.type}
                </p>
              )}
            </div>

            {/* Unit Selection Field */}
            <div>
              <label
                htmlFor="unit-input"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Unit <span className="text-red-500">*</span>
              </label>
              <select
                id="unit-input"
                name="unit"
                value={formData.unit || 'ft'}
                onChange={handleInputChange}
                disabled={mode === 'view'}
                required={mode !== 'view'}
                className={clsx(
                  'input input-bordered w-full h-10',
                  errors.unit && 'border-red-500 focus:ring-red-500'
                )}
                autoComplete="off"
                aria-invalid={!!errors.unit}
                aria-describedby={errors.unit ? 'unit-error' : undefined}
                aria-required="true"
                aria-label="Measurement unit"
              >
                <option value="ft">Feet (ft)</option>
                <option value="m">Meters (m)</option>
              </select>
              {errors.unit && (
                <p
                  id="unit-error"
                  className="text-red-500 text-sm mt-1"
                  role="alert"
                >
                  {errors.unit}
                </p>
              )}
            </div>

            {/* Length Field */}
            <div>
              <label
                htmlFor="length-input"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Length ({formData.unit || 'ft'}){' '}
                <span className="text-red-500">*</span>
                {isCustomModel && (
                  <span className="text-blue-600 text-xs ml-1">(Editable)</span>
                )}
              </label>
              <NumberInput
                id="length-input"
                name="length"
                value={formData.length ?? ''}
                onValueChange={handleDimensionChange('length')}
                disabled={isFieldDisabled()}
                className={clsx(
                  'input input-bordered w-full h-10',
                  errors.length && 'border-red-500 focus:ring-red-500'
                )}
                autoComplete="off"
                aria-invalid={!!errors.length}
                aria-describedby={errors.length ? 'length-error' : undefined}
                aria-required="true"
                placeholder={`Enter length in ${formData.unit || 'ft'}`}
                min={0}
                step={0.01}
                maxLength={10}
              />
              {errors.length && (
                <p
                  id="length-error"
                  className="text-red-500 text-sm mt-1"
                  role="alert"
                >
                  {errors.length}
                </p>
              )}
            </div>

            {/* Width Field */}
            <div>
              <label
                htmlFor="width-input"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Width ({formData.unit || 'ft'}){' '}
                <span className="text-red-500">*</span>
                {isCustomModel && (
                  <span className="text-blue-600 text-xs ml-1">(Editable)</span>
                )}
              </label>
              <NumberInput
                id="width-input"
                name="width"
                value={formData.width ?? ''}
                onValueChange={handleDimensionChange('width')}
                disabled={isFieldDisabled()}
                className={clsx(
                  'input input-bordered w-full h-10',
                  errors.width && 'border-red-500 focus:ring-red-500'
                )}
                autoComplete="off"
                aria-invalid={!!errors.width}
                aria-describedby={errors.width ? 'width-error' : undefined}
                aria-required="true"
                placeholder={`Enter width in ${formData.unit || 'ft'}`}
                min={0}
                step={0.01}
                maxLength={10}
              />
              {errors.width && (
                <p
                  id="width-error"
                  className="text-red-500 text-sm mt-1"
                  role="alert"
                >
                  {errors.width}
                </p>
              )}
            </div>

            {/* Draft Field */}
            <div>
              <label
                htmlFor="draft-input"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Draft ({formData.unit || 'ft'})
                {isCustomModel && (
                  <span className="text-blue-600 text-xs ml-1">(Editable)</span>
                )}
              </label>
              <NumberInput
                id="draft-input"
                name="draft"
                value={formData.draft ?? ''}
                onValueChange={handleDimensionChange('draft')}
                disabled={isFieldDisabled()}
                className={clsx(
                  'input input-bordered w-full h-10',
                  errors.draft && 'border-red-500 focus:ring-red-500'
                )}
                autoComplete="off"
                aria-invalid={!!errors.draft}
                aria-describedby={errors.draft ? 'draft-error' : undefined}
                placeholder={`Enter draft in ${formData.unit || 'ft'}`}
                min={0}
                step={0.01}
                maxLength={10}
              />
              {errors.draft && (
                <p
                  id="draft-error"
                  className="text-red-500 text-sm mt-1"
                  role="alert"
                >
                  {errors.draft}
                </p>
              )}
            </div>
            {/* Height Field */}
            <div>
              <label
                htmlFor="height-input"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Height ({formData.unit || 'ft'})
                {isCustomModel && (
                  <span className="text-blue-600 text-xs ml-1">(Editable)</span>
                )}
              </label>
              <NumberInput
                id="height-input"
                name="height"
                value={formData.height ?? ''}
                onValueChange={handleDimensionChange('height')}
                disabled={isFieldDisabled()}
                className={clsx(
                  'input input-bordered w-full h-10',
                  errors.height && 'border-red-500 focus:ring-red-500'
                )}
                autoComplete="off"
                aria-invalid={!!errors.height}
                aria-describedby={errors.height ? 'height-error' : undefined}
                placeholder={`Enter height in ${formData.unit || 'ft'}`}
                min={0}
                step={0.01}
                maxLength={10}
              />
              {errors.height && (
                <p
                  id="height-error"
                  className="text-red-500 text-xs mt-1"
                  role="alert"
                >
                  {errors.height}
                </p>
              )}
            </div>

            {/* Weight */}
            <div className="flex flex-col">
              <label
                htmlFor="weight-input"
                className="text-sm font-medium text-gray-700 mb-1"
              >
                Weight (kg)
              </label>
              <div className="relative">
                <NumberInput
                  id="weight-input"
                  name="weight"
                  value={formData.weight ?? ''}
                  onValueChange={handleDimensionChange('weight')}
                  disabled={isSubmitting || mode === 'view'}
                  className={clsx(
                    'input input-bordered w-full h-10 pr-12',
                    errors.weight && 'border-red-500 focus:ring-red-500'
                  )}
                  autoComplete="off"
                  aria-invalid={!!errors.weight}
                  aria-describedby={errors.weight ? 'weight-error' : undefined}
                  placeholder={`Enter weight in kg`}
                  min={0}
                  step={0.01}
                  maxLength={10}
                />
                <div className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm pointer-events-none">
                  kg
                </div>
              </div>
              {errors.weight && (
                <p
                  id="weight-error"
                  className="text-red-500 text-xs mt-1"
                  role="alert"
                >
                  {errors.weight}
                </p>
              )}
            </div>

            {/* Registration Number Field */}
            <div>
              <label
                htmlFor="registrationNumber-input"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Registration Number <span className="text-red-500">*</span>
              </label>
              <input
                id="registrationNumber-input"
                type="text"
                name="registrationNumber"
                value={formData.registrationNumber || ''}
                onChange={handleInputChange}
                disabled={mode === 'view'}
                required={mode !== 'view'}
                className={clsx(
                  'input input-bordered w-full h-10',
                  errors.registrationNumber &&
                    'border-red-500 focus:ring-red-500'
                )}
                autoComplete="off"
                aria-invalid={!!errors.registrationNumber}
                aria-describedby={
                  errors.registrationNumber
                    ? 'registrationNumber-error'
                    : undefined
                }
                aria-required="true"
                placeholder="Enter registration number"
              />
              {errors.registrationNumber && (
                <p
                  id="registrationNumber-error"
                  className="text-red-500 text-sm mt-1"
                  role="alert"
                >
                  {errors.registrationNumber}
                </p>
              )}
            </div>

            {/* Boater Field */}
            <div>
              <label
                htmlFor="boater-id"
                id="boater-label"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Boater <span className="text-red-500">*</span>
              </label>
              <div className="relative" ref={boaterDropdownRef}>
                <div
                  className={clsx(
                    'input input-bordered w-full h-10 flex items-center justify-between cursor-pointer',
                    isBoaterDropdownOpen &&
                      !isUsersLoading &&
                      mode !== 'view' &&
                      'border-primary',
                    errors.boaterId && 'border-red-500 focus:ring-red-500',
                    (mode === 'view' || isUsersLoading) &&
                      'bg-gray-100 cursor-not-allowed'
                  )}
                  onClick={handleBoaterDropdownToggle}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleBoaterDropdownToggle();
                    } else if (e.key === 'Escape' && isBoaterDropdownOpen) {
                      setIsBoaterDropdownOpen(false);
                    } else if (e.key === 'ArrowDown' && !isBoaterDropdownOpen) {
                      e.preventDefault();
                      setIsBoaterDropdownOpen(true);
                    }
                  }}
                  tabIndex={0}
                  aria-label="Select boater"
                  aria-invalid={!!errors.boaterId}
                  aria-describedby={
                    errors.boaterId ? 'boater-error' : undefined
                  }
                  aria-haspopup="listbox"
                  aria-expanded={isBoaterDropdownOpen}
                  aria-controls="boater-options-list"
                  aria-required="true"
                  role="combobox"
                  id="boater-id"
                >
                  <div className="truncate">
                    {formData.boater?.name
                      ? formData.boater.name
                      : isUsersLoading
                        ? 'Loading boaters...'
                        : formData.boater?.email
                          ? formData.boater.email
                          : 'Select boater'}
                  </div>
                  <div className="flex items-center">
                    <svg
                      className={clsx(
                        'w-4 h-4 text-gray-400 transition-transform duration-200',
                        isBoaterDropdownOpen && 'transform rotate-180'
                      )}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      aria-hidden="true"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </div>
                </div>

                {isBoaterDropdownOpen && !isUsersLoading && mode !== 'view' && (
                  <div
                    id="boater-options-list"
                    className="absolute z-50 w-full bottom-[110%] bg-gray-100 border border-gray-300 rounded-md shadow-lg max-h-[200px] overflow-y-auto"
                    role="listbox"
                    aria-label="Boater options"
                  >
                    {isUsersLoading ? (
                      <div className="p-2 text-gray-500" role="status">
                        Loading boaters...
                      </div>
                    ) : boaterOptions.length > 0 ? (
                      boaterOptions.map((boater, index) => (
                        <div
                          key={boater.id}
                          className={clsx(
                            'px-3 py-2 text-sm cursor-pointer hover:bg-gray-100 focus:bg-gray-100 focus:outline-none',
                            formData.boater?.id === boater.id &&
                              'bg-primary-light'
                          )}
                          onClick={() => handleBoaterSelect(boater)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              handleBoaterSelect(boater);
                            } else if (e.key === 'ArrowDown') {
                              e.preventDefault();
                              const nextIndex =
                                (index + 1) % boaterOptions.length;
                              const nextElement = e.currentTarget.parentElement
                                ?.children[nextIndex] as HTMLElement;
                              nextElement?.focus();
                            } else if (e.key === 'ArrowUp') {
                              e.preventDefault();
                              const prevIndex =
                                index === 0
                                  ? boaterOptions.length - 1
                                  : index - 1;
                              const prevElement = e.currentTarget.parentElement
                                ?.children[prevIndex] as HTMLElement;
                              prevElement?.focus();
                            } else if (e.key === 'Escape') {
                              setIsBoaterDropdownOpen(false);
                              // Focus back to the combobox
                              const combobox = e.currentTarget
                                .closest('.relative')
                                ?.querySelector(
                                  '[role="combobox"]'
                                ) as HTMLElement;
                              combobox?.focus();
                            }
                          }}
                          tabIndex={0}
                          role="option"
                          aria-selected={formData.boater?.id === boater.id}
                        >
                          {boater.email}
                          {boater.name ? ` (${boater.name})` : ''}
                        </div>
                      ))
                    ) : (
                      <div
                        className="px-3 py-2 text-sm text-gray-500"
                        role="status"
                      >
                        No boaters found
                      </div>
                    )}
                  </div>
                )}
              </div>
              {errors.boaterId && (
                <p
                  id="boater-error"
                  className="text-red-500 text-sm mt-1"
                  role="alert"
                >
                  {errors.boaterId}
                </p>
              )}
            </div>

            {/* Created At Field */}
            {mode !== 'create' && (
              <div>
                <label
                  htmlFor="createdAt"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Created At
                </label>
                <input
                  id="createdAt"
                  name="createdAt"
                  value={
                    formData.createdAt
                      ? formatDisplayDate(formData.createdAt, '')
                      : ''
                  }
                  disabled={true}
                  className="input input-bordered w-full h-10"
                  autoComplete="off"
                  aria-label="Boat creation date"
                />
              </div>
            )}
          </div>
        </div>
        {(mode === 'create' || mode === 'edit') && (
          <div
            className="mt-6 flex justify-end gap-2"
            role="group"
            aria-label="Form actions"
          >
            <button
              type="button"
              onClick={handleCancelClick}
              className="btn btn-sm btn-light"
              aria-label="Cancel and close form"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              onClick={handleSubmit}
              className="btn btn-sm btn-primary"
              aria-label={
                mode === 'create' ? 'Create new boat' : 'Save boat changes'
              }
              disabled={isSubmitting}
              aria-busy={isSubmitting}
            >
              {isSubmitting
                ? 'Saving...'
                : mode === 'create'
                  ? 'Create'
                  : 'Save'}
            </button>
          </div>
        )}
      </div>
    </Modal>
  );
};

export { BoatModal };
